import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Modal,
  ModalDialog,
  Stack,
} from "@mui/joy";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { UserSimField } from "api/userSimApi";
import { AutoFillValues, getAutoFillProgress } from "logic/autoFill";
import { AutoFillStep } from "hooks/useAutoFillStream";
import useIsInappWebview from "hooks/useIsInappWebview";
import CustomTypography from "components/common/CustomTypography";
import { renderInlineBold } from "components/common/SimpleMdToTypo";
import { sendMessage } from "utils/webview";
import { addAlpha } from "styles/hexOpacity";
import { theme } from "styles/theme";

// 최근 단계만 보여준다 (패널 높이)
const MAX_STEPS = 4;

// 자동 채우기 진행 패널 — 확인 창이 아니라 진행 상태 표시. 화면 가운데, 너비는 CustomModal 과 같게 (모바일 320px, PC 480px).
// 뒤 화면은 잠근다(빈 공간을 눌러도 닫히지 않음). 다 쓰면 부모가 닫는다.
export default function AutoFillProgress({
  open,
  isStopping,
  onStop,
  steps,
  fields,
  values,
  currentKey,
}: {
  open: boolean;
  isStopping: boolean;
  onStop: () => void;
  steps: AutoFillStep[];
  fields: UserSimField[];
  values: AutoFillValues;
  currentKey?: string;
}) {
  const isMobile = useIsInappWebview();
  const fieldMap = new Map(fields.map((field) => [field.key, field]));
  const progress = getAutoFillProgress(fields, values);
  // 방금 쓴 칸 — 마지막 STATE_DELTA 의 칸 (고쳐 쓴 칸은 key 순서가 바뀌지 않아 values 로는 알 수 없다)
  const latestField = currentKey ? fieldMap.get(currentKey) : undefined;
  const visibleSteps = steps.slice(-MAX_STEPS);
  // 생각 요약은 마지막으로 받은 글 하나만, 그 글의 단계 아래에 — 다음 글이 올 때까지 둔다 (계획은 끝나자마자 다음 단계가 열린다)
  const reasoningIndex = visibleSteps
    .map((step) => !!step.reasoning?.trim())
    .lastIndexOf(true);

  // 경과 시간
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [open]);
  const secondsSince = (time?: number) =>
    time ? Math.max(0, Math.floor((now - time) / 1000)) : 0;

  // 앱(WebView)에 모달 상태 전달 — CustomModal 과 같은 이벤트
  useEffect(() => {
    if (!window.ReactNativeWebView) return;
    const send = (status: "open" | "close") =>
      sendMessage(JSON.stringify({ event: "modal", params: { status } }));
    send(open ? "open" : "close");
    return () => send("close");
  }, [open]);

  return (
    <Modal
      open={open}
      // 빈 공간을 눌러도 닫지 않는다 — 중지 버튼으로만
      onClose={() => {}}
      sx={{
        "& .MuiModal-backdrop": {
          backgroundColor: addAlpha("#000000", 0.5),
        },
      }}
    >
      <ModalDialog
        aria-label="자동 채우기 진행"
        sx={{
          width: isMobile ? "320px" : "480px",
          maxWidth: "calc(100vw - 32px)",
          borderRadius: "24px",
          border: 0,
          boxShadow: theme.shadow.lg,
          backgroundColor: theme.vars.palette.Surface.Forward,
          // CustomModal 과 같은 여백
          px: isMobile ? "20px" : "32px",
          py: isMobile ? "24px" : "32px",
          gap: 0,
        }}
      >
        <Stack spacing={2}>
          {/* 상태 */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Spinner size="24px" />
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {isStopping ? "중지하고 있어요" : "활동지를 채우고 있어요"}
              </CustomTypography>
              <CustomTypography
                variant={"Caption"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                다 만들면 바로 채워져요
              </CustomTypography>
            </Stack>
            <Button
              size="sm"
              variant="Plain"
              loading={isStopping}
              onClick={onStop}
              sx={{ flexShrink: 0 }}
            >
              중지
            </Button>
          </Stack>

          {/* 진행률 — 지금 보이는 필수 칸 기준. 첫 값이 오기 전에는 진행률 미정 */}
          <Stack spacing={1}>
            <LinearProgress
              {...(progress.written > 0
                ? {
                    determinate: true,
                    value:
                      progress.total > 0
                        ? (progress.written / progress.total) * 100
                        : 0,
                  }
                : {})}
              // components/onboarding/ProgressBar 와 같은 모양 (진행률 미정 표시가 필요해 Joy LinearProgress 로)
              variant="plain"
              sx={{
                "--LinearProgress-thickness": "8px",
                "--LinearProgress-radius": "100px",
                "--LinearProgress-progressRadius": "100px",
                backgroundColor: theme.vars.palette.Surface.Backward,
                color: theme.vars.palette.Surface.Highlight,
              }}
            />
            {progress.total > 0 && (
              <CustomTypography
                variant={"Caption"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                  textAlign: "right",
                }}
              >
                {`${progress.written} / ${progress.total}칸`}
              </CustomTypography>
            )}
          </Stack>

          {/* 단계 */}
          <Stack
            spacing={1}
            sx={{
              p: "12px 16px",
              borderRadius: "12px",
              backgroundColor: theme.vars.palette.Background.Level1,
            }}
          >
            {visibleSteps.map((step, index) => (
              <Stack key={`${step.name}_${index}`} spacing={0.75}>
                <StepRow running={step.status === "running"}>
                  {step.label}
                  {step.status === "running" &&
                    secondsSince(step.startedAt) > 0 && (
                      <Box
                        component="span"
                        sx={{
                          ml: 0.75,
                          fontWeight: 500,
                          color: theme.vars.palette.Content.Weak,
                        }}
                      >
                        {`${secondsSince(step.startedAt)}초`}
                      </Box>
                    )}
                </StepRow>
                {step.tools.map((tool) => (
                  <Box key={tool.id} sx={{ pl: "24px" }}>
                    <StepRow running={tool.status === "running"} small>
                      {tool.label}
                    </StepRow>
                  </Box>
                ))}
                {index === reasoningIndex && step.reasoning && (
                  <Reasoning text={step.reasoning} />
                )}
              </Stack>
            ))}
          </Stack>

          {/* 방금 쓴 칸 */}
          {currentKey && values[currentKey] !== undefined && (
            <Stack spacing={0.5} sx={{ px: "4px" }}>
              <CustomTypography
                variant={"Footnote"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Weak,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {`방금 쓴 칸 · ${getFieldTitle(latestField) ?? ""}`}
              </CustomTypography>
              <CustomTypography
                variant={"Caption"}
                sx={{
                  fontWeight: 500,
                  color: theme.vars.palette.Content.Medium,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {getValueText(latestField, values[currentKey])}
              </CustomTypography>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

function Spinner({ size }: { size: string }) {
  return (
    <CircularProgress
      sx={{
        flexShrink: 0,
        "--CircularProgress-size": size,
        "--CircularProgress-trackThickness": "2px",
        "--CircularProgress-progressThickness": "2px",
        "--CircularProgress-trackColor": theme.vars.palette.Surface.Backward,
        "--CircularProgress-progressColor":
          theme.vars.palette.Surface.Highlight,
      }}
    />
  );
}

// 생각 요약 — 흘러나오는 글. 최근 5줄이 보이게 아래로 따라가고, 위로 올려 읽는 중이면 멈춘다
function Reasoning({ text }: { text: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const followRef = useRef(true);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 새 글로 바뀌어 넘치지 않으면 다시 따라간다
    if (el.scrollHeight <= el.clientHeight) followRef.current = true;
    if (followRef.current) el.scrollTop = el.scrollHeight;
    setScrolled(el.scrollTop > 0);
  }, [text]);

  return (
    <Box
      ref={ref}
      onScroll={(e) => {
        const el = e.currentTarget;
        followRef.current =
          el.scrollHeight - el.scrollTop - el.clientHeight < 8;
        setScrolled(el.scrollTop > 0);
      }}
      sx={{
        pl: "24px",
        // Footnote 줄 높이(12px × 126%) × 5줄
        maxHeight: "76px",
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
        // 위로 밀려난 글은 흐리게
        ...(scrolled && {
          maskImage: "linear-gradient(to bottom, transparent, black 16px)",
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent, black 16px)",
        }),
      }}
    >
      <CustomTypography
        variant={"Footnote"}
        sx={{
          fontWeight: 500,
          color: theme.vars.palette.Content.Weak,
          whiteSpace: "pre-line",
          wordBreak: "break-word",
        }}
      >
        {/* 문단 사이 빈 줄은 줄인다. **제목** 은 굵게 */}
        {renderInlineBold(text.trim().replace(/\n\s*\n/g, "\n"))}
      </CustomTypography>
    </Box>
  );
}

function StepRow({
  running,
  small,
  children,
}: {
  running: boolean;
  small?: boolean;
  children: React.ReactNode;
}) {
  const isCurrent = running && !small;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      {running ? (
        <Spinner size="16px" />
      ) : (
        <CheckRoundedIcon
          sx={{ fontSize: "16px", color: theme.vars.palette.Content.Highlight }}
        />
      )}
      <CustomTypography
        variant={small ? "Footnote" : "Caption"}
        sx={{
          fontWeight: isCurrent ? 700 : 500,
          color: isCurrent
            ? theme.vars.palette.Content.Strong
            : theme.vars.palette.Content.Medium,
        }}
      >
        {children}
      </CustomTypography>
    </Stack>
  );
}

// 칸 이름 — 이름표 → 한글 칸 이름 → 안내 문구 순 (생각기록지 칸 이름은 emotion_domain 같은 영문 id)
function getFieldTitle(field?: UserSimField) {
  if (!field) return undefined;
  if (field.label) return field.label;
  if (field.name && /[가-힣]/.test(field.name)) return field.name;
  return field.question || field.name || undefined;
}

function getValueText(
  field: UserSimField | undefined,
  value: AutoFillValues[string]
) {
  if (field?.input === "choice" && typeof value === "number") {
    return field.options[value] ?? `${value}`;
  }
  if (field?.input === "multi_choice" && Array.isArray(value)) {
    return value.map((index) => field.options[index] ?? index).join(", ");
  }
  return `${value}`;
}
